import { fetchAdminApi } from "./api";

export interface RecalculateRequest {
  organizationId: string;
  employeeIds: string[];
  startDate: string;
  endDate: string;
  actions: ("attendance" | "sessionSummaries" | "dailyReports" | "timesheets")[];
}

export interface EmployeePreviewItem {
  employeeId: string;
  employeeName: string;
  email: string;
  contractType: string;
  shiftName: string | null;
  shiftTimes: string | null;
  breakAllowanceMin: number;
  gracePeriodMin: number;
  useProductiveHours: boolean;
  salaryBasis: string;
  sessionsInRange: number;
}

export interface PreviewResult {
  employeeSummary: EmployeePreviewItem[];
  attendanceDays: {
    total: number;
    toRecalculate: number;
    manuallySkipped: number;
    records: Array<{
      employeeName: string;
      date: string;
      manuallyCorrected: boolean;
      clockIn: string | null;
      clockOut: string | null;
      before: { status: string; workedMin: number; lateMin: number; earlyOutMin: number; otMin: number; breakMin: number; windowStartUtc: string | null; windowEndUtc: string | null };
      after: { status: string; workedMin: number; lateMin: number; earlyOutMin: number; otMin: number; breakMin: number; windowStartUtc: string | null; windowEndUtc: string | null } | null;
      changed: boolean;
      error: string | null;
    }>;
  } | null;
  sessionSummaries: {
    total: number;
    willChange: number;
    samples: Array<{
      employeeName: string;
      sessionId: string;
      date: string;
      clockIn: string | null;
      clockOut: string | null;
      sessionDurationH: number;
      before: { productiveH: number; unproductiveH: number; neutralH: number; idleH: number; breakH: number };
      after: { productiveH: number; unproductiveH: number; neutralH: number; idleH: number; breakH: number };
    }>;
  } | null;
  dailyReports: {
    total: number;
    toRegenerate: number;
    records: Array<{
      employeeName: string;
      date: string;
      currentScore: number;
      currentGrade: string;
      productiveTimeH: number;
      unproductiveTimeH: number;
      idleTimeH: number;
      totalWorkTimeH: number;
      breakTimeH: number;
      offlineTimeH: number;
      overshootBreakH: number;
      clockIn: string | null;
      clockOut: string | null;
    }>;
  } | null;
  timesheets: {
    total: number;
    draftsToRegenerate: number;
    lockedSkipped: number;
    records: Array<{
      employeeName: string;
      period: string;
      status: string;
      totalWorkedH: number;
      totalProductiveH: number;
      totalOvertimeH: number;
      daysWorked: number;
    }>;
  } | null;
}

export interface JobStatus {
  status: string;
  progress: { phase: string; current: number; total: number } | null;
  results: {
    attendanceDays: { recalculated: number; skipped: number; errors: number } | null;
    sessionSummaries: { processed: number; changed: number } | null;
    dailyReports: { regenerated: number } | null;
    timesheets: { regenerated: number; skippedLocked: number } | null;
  } | null;
  failedReason: string | null;
}

export async function previewRecalculation(request: RecalculateRequest): Promise<PreviewResult> {
  return fetchAdminApi<PreviewResult>("/recalculate/preview", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function applyRecalculation(request: RecalculateRequest): Promise<{ jobId: string }> {
  return fetchAdminApi<{ jobId: string }>("/recalculate/apply", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function getRecalculationStatus(jobId: string): Promise<JobStatus> {
  return fetchAdminApi<JobStatus>(`/recalculate/status/${jobId}`);
}
